/**
 * 規劃器 Canvas
 *
 * 提供家具視覺化和互動功能：
 * - 繪製所有家具（辦公桌、會議室、主管室）
 * - 選取、拖曳、旋轉家具
 * - 繪製門淨空區域、走道、柱子等
 */

import { BaseCanvas, type CanvasOptions } from './base-canvas';
import {
  drawLine,
  fillRect,
  drawRect,
  fillCircle,
  drawCircle,
  fillPolygon,
  drawPolygon,
  drawText,
} from './drawing-utils';
import { FURNITURE } from '@/config/constants';
import {
  distance,
  isPointInRotatedRect,
  rotatePoint,
  radiansToDegrees,
} from '@/utils/geometry';
import {
  plannerStore,
  selectFurniture,
  updateFurniturePosition,
  updateFurnitureRotation,
  setIsDragging,
  setIsRotating,
  type PlannerState,
} from '@/store/planner-store';
import type { Point, Rectangle, DoorClearanceZone, Pillar } from '@/types';
import type { FurnitureObject, CorridorZone, Aisle } from '@/types/furniture';

// ========================================================================
// 型別定義
// ========================================================================

export interface PlannerCanvasOptions extends CanvasOptions {
  /** 是否啟用互動（拖曳、旋轉） */
  enableInteraction?: boolean;
  /** 是否顯示淨空區域 */
  showClearanceZones?: boolean;
  /** 是否顯示走道 */
  showCorridors?: boolean;
  /** 是否顯示柱子 */
  showPillars?: boolean;
  /** 是否顯示尺寸 */
  showDimensions?: boolean;
  /** 是否顯示房間輪廓（除錯用） */
  showRoomOutline?: boolean;
  /** 是否顯示辦公座位區 */
  showOfficeAreas?: boolean;
}

// ========================================================================
// PlannerCanvas 類別
// ========================================================================

export class PlannerCanvas extends BaseCanvas {
  /** 是否啟用互動 */
  private enableInteraction: boolean;

  /** 顯示選項 */
  private showClearanceZones: boolean;
  private showCorridors: boolean;
  private showPillars: boolean;
  private showDimensions: boolean;
  private showRoomOutline: boolean;
  private showOfficeAreas: boolean;

  /** 拖曳狀態 */
  private isDragging: boolean = false;
  private dragStartPoint: Point | null = null;
  private dragStartFurniturePos: Point | null = null;

  /** 旋轉狀態 */
  private isRotating: boolean = false;

  /** Store 取消訂閱函式 */
  private unsubscribe: (() => void) | null = null;

  constructor(options: PlannerCanvasOptions) {
    super({ ...options, cursor: 'default' });
    this.enableInteraction = options.enableInteraction ?? true;
    // 預設不顯示這些輔助元素（與 HTML 版本一致）
    this.showClearanceZones = options.showClearanceZones ?? false;
    this.showCorridors = options.showCorridors ?? false;
    this.showPillars = options.showPillars ?? false;
    this.showDimensions = options.showDimensions ?? false;
    this.showRoomOutline = options.showRoomOutline ?? false;
    this.showOfficeAreas = options.showOfficeAreas ?? false;
  }

  // ========================================================================
  // 初始化
  // ========================================================================

  initialize(): void {
    if (this.enableInteraction) {
      this.bindPointerEvents({
        onPointerDown: this.onPointerDown.bind(this),
        onPointerMove: this.onPointerMove.bind(this),
        onPointerUp: this.onPointerUp.bind(this),
        onDoubleClick: this.onDoubleClick.bind(this),
      });
    }

    // 訂閱 Store 變更
    this.unsubscribe = plannerStore.subscribe(() => {
      this.render();
    });
  }

  // ========================================================================
  // 事件處理
  // ========================================================================

  private onPointerDown(point: Point, _event: PointerEvent): void {
    const state = plannerStore.getState();

    // 檢查是否點擊到旋轉手柄
    if (state.selectedObject) {
      const handlePos = this.getRotationHandlePosition(state.selectedObject);
      if (distance(point, handlePos) < 15 / this.scale) {
        this.isRotating = true;
        setIsRotating(true);
        return;
      }
    }

    // 檢查是否點擊到家具
    const clickedObject = this.findFurnitureAtPoint(point);
    if (clickedObject) {
      selectFurniture(clickedObject);
      this.isDragging = true;
      this.dragStartPoint = point;
      this.dragStartFurniturePos = { x: clickedObject.x, y: clickedObject.y };
      setIsDragging(true);
      this.canvas.style.cursor = 'grabbing';
    } else {
      selectFurniture(null);
    }

    this.render();
  }

  private onPointerMove(point: Point, _event: PointerEvent): void {
    const state = plannerStore.getState();

    if (this.isRotating && state.selectedObject) {
      // 計算旋轉角度
      // 注意：obj.x, obj.y 已經是中心點
      const center = {
        x: state.selectedObject.x,
        y: state.selectedObject.y,
      };
      const angle = Math.atan2(point.y - center.y, point.x - center.x);
      updateFurnitureRotation(state.selectedObject.id, angle + Math.PI / 2);
      return;
    }

    if (this.isDragging && state.selectedObject && this.dragStartPoint && this.dragStartFurniturePos) {
      // 計算位移
      const dx = point.x - this.dragStartPoint.x;
      const dy = point.y - this.dragStartPoint.y;
      updateFurniturePosition(
        state.selectedObject.id,
        this.dragStartFurniturePos.x + dx,
        this.dragStartFurniturePos.y + dy
      );
      return;
    }

    // 更新游標
    const hoveredObject = this.findFurnitureAtPoint(point);
    if (hoveredObject) {
      this.canvas.style.cursor = 'grab';
    } else {
      this.canvas.style.cursor = 'default';
    }
  }

  private onPointerUp(_point: Point, _event: PointerEvent): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.dragStartPoint = null;
      this.dragStartFurniturePos = null;
      setIsDragging(false);
      this.canvas.style.cursor = 'grab';
    }

    if (this.isRotating) {
      this.isRotating = false;
      setIsRotating(false);
    }
  }

  private onDoubleClick(point: Point, _event: PointerEvent): void {
    // 雙擊重設家具到原始位置
    const clickedObject = this.findFurnitureAtPoint(point);
    if (clickedObject) {
      updateFurniturePosition(clickedObject.id, clickedObject.originalX, clickedObject.originalY);
      updateFurnitureRotation(clickedObject.id, clickedObject.originalRotation);
    }
  }

  // ========================================================================
  // 家具查找
  // ========================================================================

  /**
   * 查找點擊位置的家具
   * 注意：obj.x, obj.y 是物件的中心點座標
   */
  private findFurnitureAtPoint(point: Point): FurnitureObject | null {
    const state = plannerStore.getState();

    // 從後往前遍歷（後繪製的在上層）
    for (let i = state.furnitureObjects.length - 1; i >= 0; i--) {
      const obj = state.furnitureObjects[i];
      if (!obj) continue;

      // obj.x, obj.y 是中心點，轉換為左上角
      const rect: Rectangle = {
        x: obj.x - obj.width / 2,
        y: obj.y - obj.height / 2,
        width: obj.width,
        height: obj.height,
      };

      if (isPointInRotatedRect(point, rect, obj.rotation)) {
        return obj;
      }
    }

    return null;
  }

  /**
   * 取得旋轉手柄位置
   * 注意：obj.x, obj.y 是物件的中心點座標
   */
  private getRotationHandlePosition(obj: FurnitureObject): Point {
    // obj.x, obj.y 已經是中心點
    const center = {
      x: obj.x,
      y: obj.y,
    };
    const handleOffset = FURNITURE.ROTATION_HANDLE_DISTANCE / this.scale;
    const handlePos = {
      x: center.x,
      y: obj.y - obj.height / 2 - handleOffset,
    };
    return rotatePoint(handlePos, obj.rotation, center);
  }

  // ========================================================================
  // 字體大小計算
  // ========================================================================

  /**
   * 根據整張圖的尺寸計算合適的字體大小
   * 確保在不同縮放比例下，字體大小保持一致的視覺比例
   *
   * @param baseSize - 基礎字體大小（用於 scale=1 時的下載圖片）
   * @returns 調整後的字體大小（用於圖片座標系統）
   */
  private getScaledFontSize(baseSize: number): number {
    // 使用基礎縮放計算字體大小
    const scaledSize = baseSize / this.scale;

    // 根據圖片尺寸計算最大字體限制（避免字體過大）
    // 使用圖片較短邊的 2% 作為最大字體大小
    // 這確保所有文字大小一致，且不會超出元件邊界
    if (this.image) {
      const imageMinDimension = Math.min(this.image.width, this.image.height);
      const maxFontSize = imageMinDimension * 0.02;
      return Math.min(scaledSize, maxFontSize);
    }

    return scaledSize;
  }

  // ========================================================================
  // 渲染
  // ========================================================================

  render(): void {
    this.clear();
    this.drawImage();

    const state = plannerStore.getState();

    this.beginImageCoords();

    // 繪製房間輪廓（除錯用，與 HTML 版本一致）
    if (this.showRoomOutline && state.roomPolygon && state.roomPolygon.length > 0) {
      this.drawRoomOutline(state.roomPolygon);
    }

    // 繪製門淨空區域
    if (this.showClearanceZones) {
      for (const zone of state.doorClearanceZones) {
        this.drawClearanceZone(zone);
      }
    }

    // 繪製走道
    if (this.showCorridors) {
      for (const corridor of state.corridorZones) {
        this.drawCorridor(corridor);
      }
      for (const aisle of state.aisles) {
        this.drawAisle(aisle);
      }
    }

    // 繪製柱子
    if (this.showPillars) {
      for (const pillar of state.pillars) {
        this.drawPillar(pillar);
      }
    }

    // 繪製辦公座位區（藍色虛線）
    if (this.showOfficeAreas) {
      for (const area of state.officeAreas) {
        this.drawOfficeArea(area);
      }
    }

    // 繪製尺寸標註
    if (this.showDimensions) {
      this.drawDimensions(state);
    }

    // 繪製所有家具
    for (const obj of state.furnitureObjects) {
      this.drawFurniture(obj, obj === state.selectedObject);
    }

    // 繪製選取狀態
    if (state.selectedObject && this.enableInteraction) {
      this.drawSelectionOverlay(state.selectedObject);
    }

    this.endImageCoords();
  }

  // ========================================================================
  // 家具繪製
  // ========================================================================

  /**
   * 繪製家具物件
   */
  private drawFurniture(obj: FurnitureObject, isSelected: boolean): void {
    switch (obj.type) {
      case 'desk_chair_pair':
        this.drawDeskChairPair(obj, isSelected);
        break;
      case 'meeting_room':
      case 'supervisor_room':
        this.drawRoom(obj, isSelected);
        break;
    }
  }

  /**
   * 繪製辦公桌椅組
   * 注意：obj.x, obj.y 是物件的中心點座標（與後端 API 一致）
   */
  private drawDeskChairPair(obj: FurnitureObject, isSelected: boolean): void {
    const ctx = this.ctx;

    ctx.save();

    // 移動到物件中心（obj.x, obj.y 是中心點）
    ctx.translate(obj.x, obj.y);

    const halfWidth = obj.width / 2;
    const halfHeight = obj.height / 2;

    // 從 API 取得桌子和椅子的深度資訊
    const deskInfo = 'desk' in obj ? (obj as { desk?: { depth_meters?: number } }).desk : null;
    const chairInfo = 'chair' in obj ? (obj as { chair?: { depth_meters?: number } }).chair : null;

    const deskDepthMeters = deskInfo?.depth_meters ?? 0.6;
    const chairDepthMeters = chairInfo?.depth_meters ?? 0.5;
    const totalDepth = deskDepthMeters + chairDepthMeters;
    const deskDepthRatio = deskDepthMeters / totalDepth;
    const chairDepthRatio = chairDepthMeters / totalDepth;

    // 根據 rotation 角度決定繪製方向
    // rotation 0: 椅子在下方（正面朝北）
    // rotation π/2: 椅子在左方（正面朝東）
    // rotation π: 椅子在上方（正面朝南）
    // rotation 3π/2: 椅子在右方（正面朝西）
    const rotationDegrees = Math.round((obj.rotation * 180) / Math.PI);

    // 計算桌子和椅子的尺寸
    const deskDepth = obj.height * deskDepthRatio;
    const chairDepth = obj.height * chairDepthRatio;
    const chairWidth = obj.width / 2; // 椅子寬度是桌子寬度的 1/2

    // 根據 rotation 角度決定桌子和椅子的位置
    let deskX: number, deskY: number, deskW: number, deskH: number;
    let chairX: number, chairY: number, chairW: number, chairH: number;

    if (rotationDegrees === 0) {
      // 朝北：椅子在下方（南方）
      deskX = -halfWidth;
      deskY = -halfHeight;
      deskW = obj.width;
      deskH = deskDepth;
      chairX = -chairWidth / 2;
      chairY = deskY + deskDepth;
      chairW = chairWidth;
      chairH = chairDepth;
    } else if (rotationDegrees === 90) {
      // 朝東：椅子在左方（西方）
      deskX = -halfWidth + chairDepth;
      deskY = -halfHeight;
      deskW = deskDepth;
      deskH = obj.width;
      chairX = -halfWidth;
      chairY = -chairWidth / 2;
      chairW = chairDepth;
      chairH = chairWidth;
    } else if (rotationDegrees === 180) {
      // 朝南：椅子在上方（北方）
      deskX = -halfWidth;
      deskY = -halfHeight + chairDepth;
      deskW = obj.width;
      deskH = deskDepth;
      chairX = -chairWidth / 2;
      chairY = -halfHeight;
      chairW = chairWidth;
      chairH = chairDepth;
    } else {
      // 270 degrees 或預設：朝西，椅子在右方（東方）
      deskX = -halfWidth;
      deskY = -halfHeight;
      deskW = deskDepth;
      deskH = obj.width;
      chairX = -halfWidth + deskDepth;
      chairY = -chairWidth / 2;
      chairW = chairDepth;
      chairH = chairWidth;
    }

    // 繪製桌子
    ctx.fillStyle = FURNITURE.DESK_COLOR;
    ctx.fillRect(deskX, deskY, deskW, deskH);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.5 / this.scale;
    ctx.strokeRect(deskX, deskY, deskW, deskH);

    // 在桌子中間繪製編號
    const deskNumber = obj.name.match(/\d+/);
    if (deskNumber) {
      ctx.fillStyle = 'black';
      ctx.font = `${this.getScaledFontSize(12)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const deskCenterX = deskX + deskW / 2;
      const deskCenterY = deskY + deskH / 2;
      ctx.fillText(deskNumber[0], deskCenterX, deskCenterY);
    }

    // 繪製椅子
    ctx.fillStyle = FURNITURE.CHAIR_COLOR;
    ctx.fillRect(chairX, chairY, chairW, chairH);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.5 / this.scale;
    ctx.strokeRect(chairX, chairY, chairW, chairH);

    // 選取時繪製高亮邊框
    if (isSelected) {
      ctx.strokeStyle = FURNITURE.SELECTION_HIGHLIGHT;
      ctx.lineWidth = FURNITURE.SELECTION_LINE_WIDTH / this.scale;
      ctx.strokeRect(-halfWidth - 2 / this.scale, -halfHeight - 2 / this.scale,
                     obj.width + 4 / this.scale, obj.height + 4 / this.scale);
    }

    ctx.restore();
  }

  /**
   * 繪製會議室/主管室
   * 注意：obj.x, obj.y 是物件的中心點座標（與後端 API 一致）
   */
  private drawRoom(obj: FurnitureObject, isSelected: boolean): void {
    const ctx = this.ctx;
    const state = plannerStore.getState();
    const halfWidth = obj.width / 2;
    const halfHeight = obj.height / 2;

    ctx.save();

    // 對會議室/隔間設置房間輪廓裁切，避免牆壁超出房間邊界（與 HTML 版本一致）
    if (state.roomPolygon && state.roomPolygon.length > 0) {
      const polygon = state.roomPolygon;
      const firstPoint = polygon[0];
      if (firstPoint) {
        ctx.beginPath();
        ctx.moveTo(firstPoint.x, firstPoint.y);
        for (let i = 1; i < polygon.length; i++) {
          const point = polygon[i];
          if (point) {
            ctx.lineTo(point.x, point.y);
          }
        }
        ctx.closePath();
        ctx.clip();
      }
    }

    // 移動到物件中心（obj.x, obj.y 是中心點）
    ctx.translate(obj.x, obj.y);

    // 繪製邊框（不填充背景，與原版一致）
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.5 / this.scale;
    ctx.strokeRect(-halfWidth, -halfHeight, obj.width, obj.height);

    // 繪製會議室名稱（藍色）
    const titleFontSize = this.getScaledFontSize(16);
    ctx.fillStyle = 'rgb(37, 99, 235)';
    ctx.font = `bold ${titleFontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const textPadding = titleFontSize;
    ctx.fillText(obj.name || 'Meeting room', 0, -halfHeight + textPadding);

    // 繪製會議室尺寸標註（下緣內側和右緣內側）
    // 顯示設定的尺寸（公尺 × 100 = 公分），而非實際放置的像素尺寸
    // 這樣用戶更容易理解數字含義（例如：380 = 3.8m）
    if (state.settings) {
      const settings = state.settings;
      let widthCm: string;
      let heightCm: string;

      // 根據會議室名稱決定使用哪個設定值
      if (obj.name?.includes('大會議室')) {
        widthCm = Math.round(settings.largeMeetingRoomWidth * 100).toString();
        heightCm = Math.round(settings.largeMeetingRoomDepth * 100).toString();
      } else if (obj.name?.includes('中會議室')) {
        widthCm = Math.round(settings.mediumMeetingRoomWidth * 100).toString();
        heightCm = Math.round(settings.mediumMeetingRoomDepth * 100).toString();
      } else if (obj.name?.includes('小會議室')) {
        widthCm = Math.round(settings.smallMeetingRoomWidth * 100).toString();
        heightCm = Math.round(settings.smallMeetingRoomDepth * 100).toString();
      } else if (obj.name?.includes('主管室')) {
        widthCm = Math.round(settings.supervisorRoomWidth * 100).toString();
        heightCm = Math.round(settings.supervisorRoomDepth * 100).toString();
      } else {
        // 如果無法判斷類型，使用實際放置的尺寸作為備用
        const pixelToMeter = state.roomInfo?.pixelToMeter ?? 1;
        widthCm = Math.round((obj.width / pixelToMeter) * 100).toString();
        heightCm = Math.round((obj.height / pixelToMeter) * 100).toString();
      }

      const dimFontSize = this.getScaledFontSize(12);

      ctx.fillStyle = 'black';
      ctx.font = `${dimFontSize}px Arial`;

      // 下緣內側標註（水平文字）
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const bottomLabelY = halfHeight - dimFontSize * 0.4;
      ctx.fillText(widthCm, 0, bottomLabelY);

      // 右緣內側標註（垂直文字，旋轉 90 度）
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const rightLabelX = halfWidth - dimFontSize * 0.6;
      ctx.translate(rightLabelX, 0);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(heightCm, 0, 0);
      ctx.restore();
    }

    // 繪製門（如果有）
    const doors = 'doors' in obj ? obj.doors : undefined;
    if (doors && Array.isArray(doors) && doors.length > 0) {
      for (const door of doors) {
        // 門的座標可能有兩種格式：start/end 或 startX/startY/endX/endY
        const doorData = door as { start?: { x: number; y: number }; end?: { x: number; y: number }; startX?: number; startY?: number; endX?: number; endY?: number };

        let localStartX: number, localStartY: number, localEndX: number, localEndY: number;

        if (doorData.start && doorData.end) {
          localStartX = doorData.start.x;
          localStartY = doorData.start.y;
          localEndX = doorData.end.x;
          localEndY = doorData.end.y;
        } else if (doorData.startX !== undefined) {
          localStartX = doorData.startX;
          localStartY = doorData.startY ?? 0;
          localEndX = doorData.endX ?? 0;
          localEndY = doorData.endY ?? 0;
        } else {
          continue;
        }

        // 繪製門（白色填充矩形 + 黑色邊框）
        const dx = localEndX - localStartX;
        const dy = localEndY - localStartY;
        const doorLength = Math.sqrt(dx * dx + dy * dy);
        const doorThickness = 10 / this.scale;

        // 計算垂直方向
        let perpX = -dy / doorLength;
        let perpY = dx / doorLength;

        // 確保門向內延伸（朝向會議室中心點 (0, 0)）
        const doorMidX = (localStartX + localEndX) / 2;
        const doorMidY = (localStartY + localEndY) / 2;
        const towardsCenterX = 0 - doorMidX;
        const towardsCenterY = 0 - doorMidY;
        const dotProduct = perpX * towardsCenterX + perpY * towardsCenterY;
        if (dotProduct < 0) {
          perpX = -perpX;
          perpY = -perpY;
        }

        // 計算矩形的四個角點
        const c0 = { x: localStartX, y: localStartY };
        const c1 = { x: localEndX, y: localEndY };
        const c2 = { x: localEndX + perpX * doorThickness, y: localEndY + perpY * doorThickness };
        const c3 = { x: localStartX + perpX * doorThickness, y: localStartY + perpY * doorThickness };

        // 填充白色
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.moveTo(c0.x, c0.y);
        ctx.lineTo(c1.x, c1.y);
        ctx.lineTo(c2.x, c2.y);
        ctx.lineTo(c3.x, c3.y);
        ctx.closePath();
        ctx.fill();

        // 繪製黑色邊框
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5 / this.scale;
        ctx.stroke();
      }
    }

    // 繪製內部家具（桌椅）
    const interiorFurniture = 'interior_furniture' in obj ? obj.interior_furniture : undefined;
    if (interiorFurniture && Array.isArray(interiorFurniture) && interiorFurniture.length > 0) {
      for (const furniture of interiorFurniture) {
        const furnitureData = furniture as {
          type: string;
          x: number;
          y: number;
          width: number;
          height: number;
          rotation?: number;
          dimension_labels?: { top_edge?: string; right_edge?: string; bottom_edge?: string; left_edge?: string };
        };
        const localX = furnitureData.x;
        const localY = furnitureData.y;
        const furnitureWidth = furnitureData.width;
        const furnitureHeight = furnitureData.height;
        const furnitureType = furnitureData.type;
        const furnitureRotation = furnitureData.rotation ?? 0;

        // 根據家具類型設定顏色
        let fillColor: string;
        if (furnitureType === 'table') {
          fillColor = 'rgb(220, 220, 220)'; // 淺灰色
        } else if (furnitureType === 'chair') {
          fillColor = 'rgb(180, 180, 180)'; // 淺灰色
        } else {
          fillColor = 'rgb(200, 200, 200)';
        }

        ctx.save();

        // 移動到家具位置（本地座標）
        ctx.translate(localX, localY);

        // 如果有旋轉，繞家具中心旋轉
        if (furnitureRotation !== 0) {
          const rotationRadians = (furnitureRotation * Math.PI) / 180;
          ctx.translate(furnitureWidth / 2, furnitureHeight / 2);
          ctx.rotate(rotationRadians);
          ctx.translate(-furnitureWidth / 2, -furnitureHeight / 2);
        }

        // 繪製矩形
        ctx.fillStyle = fillColor;
        ctx.fillRect(0, 0, furnitureWidth, furnitureHeight);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1.5 / this.scale;
        ctx.strokeRect(0, 0, furnitureWidth, furnitureHeight);

        // 如果是會議桌，並且有尺寸標註資訊，則繪製尺寸標註
        if (furnitureType === 'table' && furnitureData.dimension_labels) {
          const labels = furnitureData.dimension_labels;
          const tableFontSize = this.getScaledFontSize(12);

          ctx.fillStyle = 'black';
          ctx.font = `${tableFontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          // 上緣標註（寬度）
          if (labels.top_edge) {
            const topLabelX = furnitureWidth / 2;
            const topLabelY = tableFontSize * 0.8;
            ctx.fillText(labels.top_edge, topLabelX, topLabelY);
          }

          // 右緣標註（高度）
          if (labels.right_edge) {
            ctx.save();
            const rightLabelX = furnitureWidth - tableFontSize * 0.6;
            const rightLabelY = furnitureHeight / 2;
            ctx.translate(rightLabelX, rightLabelY);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText(labels.right_edge, 0, 0);
            ctx.restore();
          }
        }

        ctx.restore();
      }
    }

    // 選取時繪製高亮邊框
    if (isSelected) {
      ctx.strokeStyle = FURNITURE.SELECTION_HIGHLIGHT;
      ctx.lineWidth = FURNITURE.SELECTION_LINE_WIDTH / this.scale;
      ctx.strokeRect(-halfWidth - 2 / this.scale, -halfHeight - 2 / this.scale,
                     obj.width + 4 / this.scale, obj.height + 4 / this.scale);
    }

    ctx.restore();
  }

  /**
   * 繪製選取覆蓋層（含旋轉手柄）
   * 注意：obj.x, obj.y 是物件的中心點座標
   */
  private drawSelectionOverlay(obj: FurnitureObject): void {
    // 繪製旋轉手柄
    const handlePos = this.getRotationHandlePosition(obj);
    // obj.x, obj.y 已經是中心點
    const center = {
      x: obj.x,
      y: obj.y,
    };

    // 繪製連接線
    drawLine(this.ctx, center, handlePos, {
      color: '#3b82f6',
      width: 2 / this.scale,
      dash: [5 / this.scale, 3 / this.scale],
    });

    // 繪製旋轉手柄
    const handleRadius = FURNITURE.ROTATION_HANDLE_RADIUS / this.scale;
    fillCircle(this.ctx, handlePos, handleRadius, { color: '#3b82f6' });
    drawCircle(this.ctx, handlePos, handleRadius, { color: '#ffffff', width: 2 / this.scale });

    // 顯示旋轉角度
    const degrees = Math.round(radiansToDegrees(obj.rotation));
    drawText(
      this.ctx,
      `${degrees}°`,
      { x: handlePos.x + 15 / this.scale, y: handlePos.y },
      {
        color: '#3b82f6',
        size: 10 / this.scale,
        baseline: 'middle',
      }
    );
  }

  // ========================================================================
  // 輔助元素繪製
  // ========================================================================

  /**
   * 繪製門淨空區域
   */
  private drawClearanceZone(zone: DoorClearanceZone): void {
    const color = zone.doorType === 'main'
      ? 'rgba(239, 68, 68, 0.2)'
      : zone.doorType === 'emergency'
        ? 'rgba(245, 158, 11, 0.2)'
        : 'rgba(59, 130, 246, 0.2)';

    fillPolygon(this.ctx, zone.points, { color });
    drawPolygon(this.ctx, zone.points, {
      color: color.replace('0.2', '0.5'),
      width: 1 / this.scale,
      dash: [5 / this.scale, 3 / this.scale],
    });
  }

  /**
   * 繪製走道
   */
  private drawCorridor(corridor: CorridorZone): void {
    fillPolygon(this.ctx, corridor.points, { color: 'rgba(34, 197, 94, 0.15)' });
    drawPolygon(this.ctx, corridor.points, {
      color: 'rgba(34, 197, 94, 0.4)',
      width: 1 / this.scale,
    });
  }

  /**
   * 繪製走道（Aisle）
   * 使用綠色，與 HTML 版本一致
   */
  private drawAisle(aisle: Aisle): void {
    // 淺綠色填充（與 HTML 版本一致）
    fillPolygon(this.ctx, aisle.points, { color: 'rgba(150, 255, 150, 0.4)' });
    // 深綠色虛線邊框
    drawPolygon(this.ctx, aisle.points, {
      color: 'rgba(0, 150, 80, 0.8)',
      width: 2 / this.scale,
      dash: [5 / this.scale, 5 / this.scale],
    });
  }

  /**
   * 繪製柱子
   */
  private drawPillar(pillar: Pillar): void {
    const rect: Rectangle = {
      x: pillar.x,
      y: pillar.y,
      width: pillar.width,
      height: pillar.height,
    };
    fillRect(this.ctx, rect, { color: 'rgba(107, 114, 128, 0.8)' });
    drawRect(this.ctx, rect, { color: '#4b5563', width: 1 / this.scale });
  }

  /**
   * 繪製辦公座位區（藍色虛線矩形）
   * 與 HTML 版本一致
   */
  private drawOfficeArea(area: { x: number; y: number; width: number; height: number }): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 255, 0.6)';
    ctx.lineWidth = 3 / this.scale;
    ctx.setLineDash([10 / this.scale, 5 / this.scale]);
    ctx.strokeRect(area.x, area.y, area.width, area.height);
    ctx.setLineDash([]);
    ctx.restore();
  }

  /**
   * 繪製房間輪廓（除錯用）
   * 使用紅色虛線繪製房間多邊形邊界
   */
  private drawRoomOutline(polygon: Point[]): void {
    const ctx = this.ctx;
    const firstPoint = polygon[0];
    if (!firstPoint) return;

    ctx.save();
    ctx.strokeStyle = '#FF0000';
    ctx.lineWidth = 3 / this.scale;
    ctx.setLineDash([5 / this.scale, 5 / this.scale]);

    ctx.beginPath();
    ctx.moveTo(firstPoint.x, firstPoint.y);
    for (let i = 1; i < polygon.length; i++) {
      const point = polygon[i];
      if (point) {
        ctx.lineTo(point.x, point.y);
      }
    }
    ctx.closePath();
    ctx.stroke();

    ctx.restore();
  }

  /**
   * 繪製尺寸標註
   */
  private drawDimensions(state: PlannerState): void {
    const { roomInfo, roomPolygon } = state;
    if (!roomInfo || !roomPolygon || roomPolygon.length === 0) return;

    // 計算房間邊界
    const minX = Math.min(...roomPolygon.map((p) => p.x));
    const maxX = Math.max(...roomPolygon.map((p) => p.x));
    const minY = Math.min(...roomPolygon.map((p) => p.y));
    const maxY = Math.max(...roomPolygon.map((p) => p.y));

    const widthPixels = maxX - minX;
    const heightPixels = maxY - minY;
    // 注意：pixelToMeter 的單位是「每公尺多少像素」，所以要用除法
    const widthMeters = widthPixels / roomInfo.pixelToMeter;
    const heightMeters = heightPixels / roomInfo.pixelToMeter;

    const fontSize = 14 / this.scale;
    const padding = 20 / this.scale;
    const arrowSize = 8 / this.scale;

    // 繪製寬度標註（底部）
    const widthY = maxY + padding;
    // 箭頭線
    drawLine(this.ctx, { x: minX, y: widthY }, { x: maxX, y: widthY }, {
      color: '#ef4444',
      width: 2 / this.scale,
    });
    // 左箭頭
    drawLine(this.ctx, { x: minX + arrowSize, y: widthY - arrowSize / 2 }, { x: minX, y: widthY }, {
      color: '#ef4444',
      width: 2 / this.scale,
    });
    drawLine(this.ctx, { x: minX + arrowSize, y: widthY + arrowSize / 2 }, { x: minX, y: widthY }, {
      color: '#ef4444',
      width: 2 / this.scale,
    });
    // 右箭頭
    drawLine(this.ctx, { x: maxX - arrowSize, y: widthY - arrowSize / 2 }, { x: maxX, y: widthY }, {
      color: '#ef4444',
      width: 2 / this.scale,
    });
    drawLine(this.ctx, { x: maxX - arrowSize, y: widthY + arrowSize / 2 }, { x: maxX, y: widthY }, {
      color: '#ef4444',
      width: 2 / this.scale,
    });
    // 寬度文字
    drawText(this.ctx, `${widthMeters.toFixed(2)} m`, { x: (minX + maxX) / 2, y: widthY + fontSize }, {
      color: '#ef4444',
      size: fontSize,
      align: 'center',
      baseline: 'top',
      bold: true,
    });

    // 繪製高度標註（右側）
    const heightX = maxX + padding;
    // 箭頭線
    drawLine(this.ctx, { x: heightX, y: minY }, { x: heightX, y: maxY }, {
      color: '#3b82f6',
      width: 2 / this.scale,
    });
    // 上箭頭
    drawLine(this.ctx, { x: heightX - arrowSize / 2, y: minY + arrowSize }, { x: heightX, y: minY }, {
      color: '#3b82f6',
      width: 2 / this.scale,
    });
    drawLine(this.ctx, { x: heightX + arrowSize / 2, y: minY + arrowSize }, { x: heightX, y: minY }, {
      color: '#3b82f6',
      width: 2 / this.scale,
    });
    // 下箭頭
    drawLine(this.ctx, { x: heightX - arrowSize / 2, y: maxY - arrowSize }, { x: heightX, y: maxY }, {
      color: '#3b82f6',
      width: 2 / this.scale,
    });
    drawLine(this.ctx, { x: heightX + arrowSize / 2, y: maxY - arrowSize }, { x: heightX, y: maxY }, {
      color: '#3b82f6',
      width: 2 / this.scale,
    });
    // 高度文字（旋轉 90 度）
    this.ctx.save();
    this.ctx.translate(heightX + fontSize, (minY + maxY) / 2);
    this.ctx.rotate(Math.PI / 2);
    drawText(this.ctx, `${heightMeters.toFixed(2)} m`, { x: 0, y: 0 }, {
      color: '#3b82f6',
      size: fontSize,
      align: 'center',
      baseline: 'bottom',
      bold: true,
    });
    this.ctx.restore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 設定是否顯示淨空區域
   */
  setShowClearanceZones(show: boolean): void {
    this.showClearanceZones = show;
    this.render();
  }

  /**
   * 設定是否顯示走道
   */
  setShowCorridors(show: boolean): void {
    this.showCorridors = show;
    this.render();
  }

  /**
   * 設定是否顯示柱子
   */
  setShowPillars(show: boolean): void {
    this.showPillars = show;
    this.render();
  }

  /**
   * 設定是否顯示尺寸
   */
  setShowDimensions(show: boolean): void {
    this.showDimensions = show;
    this.render();
  }

  /**
   * 設定是否顯示房間輪廓
   */
  setShowRoomOutline(show: boolean): void {
    this.showRoomOutline = show;
    this.render();
  }

  /**
   * 設定是否顯示辦公座位區
   */
  setShowOfficeAreas(show: boolean): void {
    this.showOfficeAreas = show;
    this.render();
  }

  /**
   * 匯出為圖片
   */
  exportAsImage(format: 'png' | 'jpeg' = 'png', quality = 0.92): string {
    return this.canvas.toDataURL(`image/${format}`, quality);
  }

  /**
   * 匯出為 Blob（使用原始圖片尺寸）
   *
   * 創建一個與原始圖片尺寸相同的 canvas，並在上面繪製所有內容，
   * 以確保輸出圖片的品質與原始圖片相同。
   */
  exportAsBlob(format: 'png' | 'jpeg' = 'png', quality = 0.92): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.image) {
        // 如果沒有圖片，使用當前 canvas
        this.canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          },
          `image/${format}`,
          quality
        );
        return;
      }

      // 創建一個與原始圖片尺寸相同的離屏 canvas
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = this.image.width;
      offscreenCanvas.height = this.image.height;
      const offscreenCtx = offscreenCanvas.getContext('2d');

      if (!offscreenCtx) {
        reject(new Error('Failed to create offscreen canvas context'));
        return;
      }

      // 繪製背景圖片（原始尺寸）
      offscreenCtx.drawImage(this.image, 0, 0);

      // 在原始尺寸的 canvas 上繪製所有家具
      // scale = 1，因為我們直接使用圖片座標
      this.renderFurnitureToContext(offscreenCtx, 1);

      // 導出為 blob
      offscreenCanvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create blob'));
        },
        `image/${format}`,
        quality
      );
    });
  }

  /**
   * 將所有可見元素繪製到指定的 canvas context 上
   * @param ctx - 目標繪圖上下文
   * @param scale - 縮放比例（1 = 原始尺寸）
   */
  private renderFurnitureToContext(ctx: CanvasRenderingContext2D, scale: number): void {
    const state = plannerStore.getState();
    const originalCtx = this.ctx;
    const originalScale = this.scale;

    // 暫時替換 context 和 scale
    this.ctx = ctx;
    this.scale = scale;

    // 繪製房間輪廓
    if (this.showRoomOutline && state.roomPolygon && state.roomPolygon.length > 0) {
      this.drawRoomOutline(state.roomPolygon);
    }

    // 繪製門淨空區域
    if (this.showClearanceZones) {
      for (const zone of state.doorClearanceZones) {
        this.drawClearanceZone(zone);
      }
    }

    // 繪製走道
    if (this.showCorridors) {
      for (const corridor of state.corridorZones) {
        this.drawCorridor(corridor);
      }
      for (const aisle of state.aisles) {
        this.drawAisle(aisle);
      }
    }

    // 繪製柱子
    if (this.showPillars) {
      for (const pillar of state.pillars) {
        this.drawPillar(pillar);
      }
    }

    // 繪製辦公座位區（藍色虛線）
    if (this.showOfficeAreas) {
      for (const area of state.officeAreas) {
        this.drawOfficeArea(area);
      }
    }

    // 繪製尺寸標註
    if (this.showDimensions) {
      this.drawDimensions(state);
    }

    // 繪製所有家具（不繪製選取狀態）
    for (const obj of state.furnitureObjects) {
      this.drawFurniture(obj, false);
    }

    // 恢復原始 context 和 scale
    this.ctx = originalCtx;
    this.scale = originalScale;
  }

  // ========================================================================
  // 生命週期
  // ========================================================================

  destroy(): void {
    this.unsubscribe?.();
    super.destroy();
  }
}
